import React, { useEffect, useRef, useState } from 'react';
import {
  Box, CircularProgress, IconButton, InputBase,
  Paper, Toolbar, Typography
} from '@mui/material';
import {
  ArrowUpward, Attachment, Close as CloseIcon, RemoveCircleOutline
} from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { AICompProps, QAList, SelectedImage, AIanswer } from './types';
import { QAitem } from './QAitem';

function safeParse(jsonString: string) {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    return '';
  }
}

function processEventChunk(chunk: string) {
  const event = {
    type: 'message', // 默认类型
    data: '',
    id: null,
    retry: null
  };
  // 按行解析事件字段
  chunk.split('\n').forEach(line => {
    if (line.startsWith('event:')) {
      event.type = line.replace('event:', '').trim();
    } else if (line.startsWith('data:')) {
      event.data += line.replace('data:', '').trim() + '\n';
    } else if (line.startsWith('id:')) {
      event.id = line.replace('id:', '').trim();
    } else if (line.startsWith('retry:')) {
      event.retry = parseInt(line.replace('retry:', '').trim(), 10);
    }
  });

  // 清理数据末尾的换行符
  const trimData = event.data.trim();
  const jsonData = safeParse(trimData);

  return {
    type: event.type,
    data: {
      id: event.id,
      retry: event.retry,
      data: jsonData,
    }
  };
}

export function AIComp(props: AICompProps) {
  const {
    tid, pimid, deviceType = 'computer', distance, showPhotoAiSearch,
    tenantName, type, toggleOpen, onClose = () => null, onOpenSmartCatalog,
    actions,
  } = props;

  const messageEndRef = useRef(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const answerAccumulatorRef = useRef<string>(''); // 使用 ref 直接累积答案，避免状态更新竞态
  const updateTimerRef = useRef<number | null>(null);
  const [value, setValue] = useState<string>(null);
  const [isIMEComposing, setIsIMEComposing] = useState<boolean>(false);
  const [qaList, setQAList] = useState<QAList[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sendEvent, setSendEvent] = useState<boolean>(false);
  const [scrollBottom, setScrollBottom] = useState<boolean>(false);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState<boolean>(true);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (updateTimerRef.current !== null) {
        clearTimeout(updateTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (sendEvent && qaList.length > 0 && qaList[qaList.length - 1].done) {
      const QaItem = qaList[qaList.length -1];
      const { question, answer } = QaItem;

      const formatAnswer: AIanswer = {
        message: [],
        references: [],
        end: []
      };
      if (typeof answer === 'string' && answer.length !== 0) {
        const chunks = answer.split(/\n\n/).filter(chunk => chunk.trim());
        for (const chunk of chunks) {
          const { type: eventType, data } = processEventChunk(chunk);
          formatAnswer[eventType].push(data);
        }
      }

      if ((formatAnswer.end[0]?.data ?? null) === null) {
        setSendEvent(false);
        return;
      }

      // 只在文本问答时发送统计事件
      if (typeof question === 'string' && actions.handleSendEvent) {
        const endData = formatAnswer.end[0]?.data;
        const body = {
          question,
          source: 'ChatTokenUsage',
          ir_req: endData?.ir_req || 0,
          ir_res: endData?.ir_res || 0,
          cg_req: endData?.cg_req || 0,
          cg_res: endData?.cg_res || 0,
        };

        actions.handleSendEvent(body).finally(() => {
          setSendEvent(false);
        });
      } else {
        setSendEvent(false);
      }
    }
  }, [qaList, sendEvent, actions, type]);

  // 处理自动滚动逻辑
  useEffect(() => {
    if (autoScrollEnabled) {
      messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [qaList, autoScrollEnabled]);

  useEffect(() => {
    if (scrollBottom && autoScrollEnabled) {
      messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setScrollBottom(false);
    }
  }, [scrollBottom, autoScrollEnabled]);

  // 检测用户滚动行为
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px容差

      // 如果用户滚动到底部，启用自动滚动
      if (isAtBottom && !autoScrollEnabled) {
        setAutoScrollEnabled(true);
      }
      // 如果用户向上滚动了一定距离，禁用自动滚动
      else if (!isAtBottom && autoScrollEnabled) {
        setAutoScrollEnabled(false);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [autoScrollEnabled]);

  const handleInput = (event: any) => {
    const trimValue = event.target.value.trim();
    setValue(trimValue === '' ? null : event.target.value);
  };

  const handleCompositionStart = () => {
    setIsIMEComposing(true);
  };

  const handleCompositionEnd = () => {
    setIsIMEComposing(false);
  };

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter' && !event.shiftKey && !isIMEComposing) {
      if (value !== null && value.trim() !== '') {
        event.preventDefault();
        setValue(null); // 立即清空输入框
        handleSubmit();
      }
    }
  };

  const handleScrollToBottom = () => {
    setTimeout(() => {
      setScrollBottom(true);
    }, 100);
  };

  const fileInputRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState<SelectedImage>(null);
  const IsImgMode = selectedImage !== null;

  const handleFileOpen = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e: any) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const file = files[0] as File;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/bmp'];
    if (!allowedTypes.includes(file.type)) {
      // 这里应该通过回调通知错误，但为了保持纯UI，我们暂时不处理
      console.error('请选择正确的图片格式');
      e.target.value = '';
      return;
    }

    const imageObject = {
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      preview: URL.createObjectURL(file),
      file,
    };

    setSelectedImage(imageObject);
    e.target.value = '';
  };

  const handleRemove = () => {
    setSelectedImage(null);
  };


  const handleSubmitImg = async () => {
    try {
      setIsLoading(true);
      const New_QaId = qaList.length + 1;
      const newQuestion: QAList = {
        type: 'image',
        id: New_QaId,
        question: selectedImage,
        answer: null,
        done: false,
      };
      setQAList(prev => [...prev, newQuestion]);

      handleRemove();

      // 调用图片搜索API
      await actions.handleImageSearch(
        `${pimid}/ai2mg`,
        selectedImage.file,
        async (contentArray: any[]) => {
          const filterArray = contentArray.filter((item: any) => (item.distance <= distance));
          const fids = filterArray.map((item: any) => item.fid);

          // 搜索匹配的产品
          const productResponse = await actions.handleSearchProductsByImage(
            `productCenter/${tid}/searchProductByPhotoIds`,
            fids,
            'zh'
          );

          setQAList((prev) => {
            prev[prev.length - 1].done = true;
            prev[prev.length - 1].answer = productResponse;
            return prev;
          });
        }
      );

    } catch (e) {
      console.error('Image search error:', e);
      setQAList((prev) => {
        prev[prev.length - 1].done = true;
        prev[prev.length - 1].answer = [];
        return prev;
      });
    } finally {
      handleScrollToBottom();
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!IsImgMode && value !== null && value.trim() !== '') {
      setScrollBottom(true);
      setIsLoading(true);
      const New_QaId = qaList.length + 1;
      const newQuestion: QAList = {
        type: 'text',
        id: New_QaId,
        question: value,
        answer: '',
        done: false,
      };
      setQAList(prev => [...prev, newQuestion]);

      // 重置答案累加器
      answerAccumulatorRef.current = '';

      // 启动定时器，定期将累积的答案更新到 UI
      const startUpdateTimer = () => {
        updateTimerRef.current = setTimeout(() => {
          if (answerAccumulatorRef.current) {
            setQAList((prev) => {
              const newList = [...prev];
              newList[newList.length - 1] = {
                ...newList[newList.length - 1],
                answer: answerAccumulatorRef.current
              };
              return newList;
            });
          }
          startUpdateTimer(); // 继续定时更新
        }, 50); // 每50ms更新一次UI
      };

      try {
        let apiMethod;
        let url;

        if (type === 'lightrag') {
          apiMethod = actions.handleFetchLightRagAnswer;
          url = `${pimid}/lightrag/chat`;
        } else {
          apiMethod = actions.handleFetchAnswer;
          url = `${pimid}/aidoc/chat`;
        }

        // 开始定时更新
        startUpdateTimer();

        await apiMethod(
          url,
          value,
          (chunk) => {
            // 直接累积到 ref，避免状态更新竞态
            answerAccumulatorRef.current += chunk;
          }
        );

        // 停止定时器
        if (updateTimerRef.current !== null) {
          clearTimeout(updateTimerRef.current);
          updateTimerRef.current = null;
        }

        // 最后一次更新，确保所有数据都被显示
        setQAList((prev) => {
          const newList = [...prev];
          newList[newList.length - 1] = {
            ...newList[newList.length - 1],
            answer: answerAccumulatorRef.current,
            done: true
          };
          return newList;
        });
        setSendEvent(true);
      } catch (err) {
        console.error('ERR', err);
        // 停止定时器
        if (updateTimerRef.current !== null) {
          clearTimeout(updateTimerRef.current);
          updateTimerRef.current = null;
        }
        // 错误时也要更新最终答案
        setQAList((prev) => {
          const newList = [...prev];
          newList[newList.length - 1] = {
            ...newList[newList.length - 1],
            answer: answerAccumulatorRef.current,
            done: true
          };
          return newList;
        });
        setSendEvent(true);
      } finally {
        setIsLoading(false);
        setValue(null);
      }
    }
    if (IsImgMode) {
      setScrollBottom(true);
      await handleSubmitImg();
    }
  };

  const InputPlaceholder = deviceType === 'computer'
    ? '输入您想知道的问题，使用Shift + Enter 换行'
    : '输入您想知道的问题';

  return (
    <Box display='flex' flexDirection='column'
      width='100%' height='100%'
    >
      <Toolbar disableGutters
        sx={{
          display: 'flex',
          flexGrow: 0,
        }}
      >
        <Typography
          variant='h6'
          align='center'
          sx={{ flex: 1, px: 2 }}
        >
          {`${tenantName}智能客服`}
        </Typography>
        <IconButton onClick={toggleOpen}
          sx={{
            position: 'absolute',
            right: 8, top: 12,
            color: grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
      </Toolbar>
      <Box ref={scrollContainerRef} display='flex' flexGrow={1} overflow='auto' sx={{ overflowX: 'hidden' }}>
        <Box width='100%' p={2.5} >
          {qaList.map(item => <QAitem key={`qaList_${item.id}`}
            tid={tid} item={item}
            onClickAsset={onClose}
            onScrollToBottom={handleScrollToBottom}
            onOpenSmartCatalog={onOpenSmartCatalog}
          />)}
          <Box height={2} mb={2} ref={messageEndRef} />
        </Box>
      </Box>
      <Box flexGrow={0} display='flex' position='relative' flexDirection='column'
        boxSizing='border-box' width='100%' pt={1} px={3} pb={3}
        sx={{ cursor: 'text' }}
      >
        <Box component={Paper} elevation={0}
          variant='outlined' bgcolor='#f5f5f5'
          display='flex' flexDirection='column' justifyContent='flex-start'
          overflow='hidden' borderRadius={3} p={1}
        >
          {!IsImgMode && <InputBase multiline
            minRows={3}
            maxRows={deviceType === 'computer' ? 10 : 5} // 限制最大行数，防止无限增高
            value={value ?? ''}
            placeholder={InputPlaceholder}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            sx={{
              overflowY: 'auto', // 超过 maxRows 后显示滚动条
              '& textarea': {
                overflowY: 'auto !important', // 强制 textarea 显示滚动条
              }
            }}
          />}
          {IsImgMode && <Box display='flex' alignItems='flex-start'>
            <img src={selectedImage.preview} alt={selectedImage.name}
              width='80px'
              height='80px'
              style={{
                objectFit: 'contain',
                borderRadius: '8px',
                border: '1px solid #eee'
              }}
            />
            <IconButton size='small' color='error'
              onClick={handleRemove}
            >
              <RemoveCircleOutline />
            </IconButton>
          </Box>}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            style={{ display: 'none' }}
          />
          <Box mt={.5} display='flex' flexDirection='row' justifyContent='flex-end' >
            {/* 搜图按钮 */}
            {showPhotoAiSearch && <IconButton size='small' disabled={isLoading}
              onClick={handleFileOpen}
              sx={{
                p: 0, mr: 3,
                '& svg': {
                  fontSize: '30px'
                }
              }}
            >
              <Attachment />
            </IconButton>}

            <IconButton size='small' disabled={isLoading}
              onClick={handleSubmit}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.main',
                },
                '&:active': {
                  bgcolor: 'primary.main',
                }
              }}
            >
              <ArrowUpward />
              {isLoading && <CircularProgress size={26} color='primary'
                sx={{
                  position: 'absolute',
                  top: '50%', left: '50%',
                  mt: '-13px', ml: '-13px'
                }}
              />}
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
